import { View, Text, ScrollView, Pressable, Alert } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";

/**
 * Admin card component
 */
interface AdminCardProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  buttonText: string;
  onPress: () => void;
  variant?: "default" | "danger";
}

const AdminCard = ({
  icon,
  title,
  description,
  buttonText,
  onPress,
  variant = "default",
}: AdminCardProps) => {
  const colors = useThemeColors();

  return (
    <View
      className="rounded-2xl p-6 mb-4"
      style={{
        backgroundColor: colors.surface,
        borderWidth: variant === "danger" ? 2 : 1,
        borderColor: variant === "danger" ? "#FCA5A5" : colors.border,
      }}
    >
      <View className="flex-row items-center mb-4">
        <Ionicons name={icon} size={24} color="#DC2626" />
        <Text className="text-lg font-semibold ml-3" style={{ color: colors.text }}>
          {title}
        </Text>
      </View>
      <Text style={{ color: colors.textSecondary }}>{description}</Text>
      <Pressable
        className="mt-4 rounded-xl py-3 items-center active:opacity-80"
        style={{
          backgroundColor: variant === "danger" ? "#DC2626" : "#FEE2E2",
        }}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={buttonText}
      >
        <Text
          className="font-semibold"
          style={{ color: variant === "danger" ? "#FFFFFF" : "#B91C1C" }}
        >
          {buttonText}
        </Text>
      </Pressable>
    </View>
  );
};

/**
 * Super Admin Dashboard
 */
export default function SuperAdminDashboard() {
  const router = useRouter();
  const colors = useThemeColors();

  const handleNavigateToUsers = () => {
    router.push("/(super-admin)/users");
  };

  const handleNavigateToWeekends = () => {
    router.push("/(super-admin)/weekends");
  };

  const handleNavigateToPushTokens = () => {
    router.push("/(super-admin)/notifications/push-tokens");
  };

  const handleResetWeekend = () => {
    Alert.alert(
      "Reset Weekend",
      "Weet je zeker dat je het weekend wilt resetten? Dit zet alle scores op 0 en maakt nieuwe teams aan.",
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Reset",
          style: "destructive",
          onPress: () => {
            // TODO: Implement reset functionality
            Alert.alert("Binnenkort", "Reset functionaliteit wordt binnenkort toegevoegd.");
          },
        },
      ]
    );
  };

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <ScrollView
        className="flex-1 px-4 pt-4"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="pb-8"
      >
        <Text className="text-2xl font-bold mb-6" style={{ color: "#DC2626" }}>
          Super Admin Dashboard
        </Text>

        <AdminCard
          icon="people"
          title="Beheer Gebruikers"
          description="Bekijk alle gebruikers, wijzig rollen, reset wachtwoorden en beheer accounts."
          buttonText="Ga naar Gebruikers"
          onPress={handleNavigateToUsers}
        />

        <AdminCard
          icon="calendar-number"
          title="Familie Weekenden"
          description="Beheer familie weekenden en activeer een nieuw weekend."
          buttonText="Ga naar Weekenden"
          onPress={handleNavigateToWeekends}
        />

        <AdminCard
          icon="notifications"
          title="Push Token Status"
          description="Bekijk welke gebruikers een actieve push token hebben en stuur test notificaties."
          buttonText="Bekijk Push Tokens"
          onPress={handleNavigateToPushTokens}
        />

        <AdminCard
          icon="refresh"
          title="Reset Weekend"
          description="⚠️ Start een nieuw weekend. Dit reset alle scores en maakt nieuwe teams aan."
          buttonText="Reset Weekend"
          onPress={handleResetWeekend}
          variant="danger"
        />
      </ScrollView>
    </SafeAreaView>
  );
}
